import React, { useState, useEffect } from 'react';
import { Modal } from 'Components/Modal';
import { SubmitButton } from 'Components/Fields/Buttons';
import {
  getPlanButtonText,
  getPlanDisplayPrice,
  getPlanSelectFrequencyLabel,
  getSimplifiedPlanDisplayName,
} from 'Utils/Plans';
import { HTMLParser } from 'Utils/Parser';
import { classCombine } from 'Utils/Utils';
import { getSetDefaultPlanIndex } from 'Utils/Analytics/Amplitude';
import { timestampToDate } from 'Utils/Format';
import { useNavigate } from 'react-router-dom';
import { selectPlan as selectPlanAction } from 'State/Plans';
import { trackEvent, TRACKING_ENUMS } from 'Utils/Analytics';
import { useStore } from 'Hooks/useStore';
import './PlanSelection.css';
import { checkAndSetSubscribeSrcParam } from 'Utils/CmpQuery';

const getDefaultHighlightedPlan = (selectedPlan, plans) => {
  return selectedPlan || plans[getSetDefaultPlanIndex(plans.length)];
};

export const PlanSelection = ({
  plans,
  selectedPlan,
  selectPlan,
  currentPlan = null,
  upcomingPlan = null,
  validityTill = null,
}) => {
  const [highlightedPlan, setHighlightedPlan] = useState(() => getDefaultHighlightedPlan(selectedPlan, plans));
  const promoCode = useStore('promoCode');
  const promoMsg = promoCode.hasApplied && promoCode.promoDescription;
  const { isAuthenticated } = useStore('user');
  const routes = useStore('appConfig.routes');
  const navigate = useNavigate();
  const { getPlansSuccess } = useStore('plans');

  /*
    This is necessary because static page may initially render with a highlighted plan
    But due to an Amplitude experiment or other factors, JS page will have a different
    highlighted plan. This useEffect allows for a rerender with the proper highlighted
    plan from JS site.
   */
  useEffect(() => {
    if (!selectedPlan) {
      setHighlightedPlan(getDefaultHighlightedPlan(selectedPlan, plans));
    }
  }, [selectedPlan, plans]);

  const submitButtonText = getPlanButtonText(!!currentPlan, upcomingPlan || highlightedPlan);
  // disable submit button if user has an upcoming plan, or no plan is selected (not including their current plan)
  const disableSubmitBtn =
    !getPlansSuccess ||
    upcomingPlan ||
    !highlightedPlan ||
    (currentPlan && highlightedPlan.planId === currentPlan.planId);

  const dispatchSelectPlan = (plan) => {
    const { planName, planPrice, planId, hasMerchandise } = plan;
    trackEvent(TRACKING_ENUMS.EVENT_PLAN_SELECT_COMPLETE, {
      page_plan_name: planName,
      page_plan_price: planPrice,
      page_plan_id: planId,
    });
    // same logic as in PlanLink component
    // default to sign up link
    let path = routes.signupCreateAccount;
    if (isAuthenticated) {
      path = routes.payment;
      if (hasMerchandise) {
        path = routes.shipping;
      }
    }

    const urlParams = new URLSearchParams();
    checkAndSetSubscribeSrcParam(urlParams);
    selectPlanAction(planId);
    navigate(`${path}${urlParams.size > 0 ? `?${urlParams.toString()}` : ''}`);
  };

  return (
    <div className="planSelection">
      {upcomingPlan && validityTill && (
        <p className="planModalTopMessage">
          Your plan is scheduled to change next billing cycle starting {timestampToDate(validityTill)}. Please wait
          until then to make additional changes.
        </p>
      )}
      {currentPlan && plans.length === 1 && (
        <p className="planModalTopMessage">You are currently subscribed to the best plan available.</p>
      )}
      {plans.map((plan) => {
        const isUpcoming = upcomingPlan && upcomingPlan.planId === plan.planId;
        const isCurrent = !upcomingPlan && currentPlan && currentPlan.planId === plan.planId;
        // current and upcoming plans are always shown as selected
        // moreover, if an upcoming plan exists, no other plan should be selected/selectable
        const isSelected = isCurrent || isUpcoming || (!upcomingPlan && highlightedPlan.planId === plan.planId);
        const { promoFeature, mainFeature, promotionPlanName, promotionPlanNameMobile, price, frequencyLabel } =
          plan.dppContent;
        const freqLabel = frequencyLabel === 'false' ? '' : frequencyLabel || getPlanSelectFrequencyLabel(plan);

        // only show promoText if either: user applied a promo code for this plan, or if user does not
        // currently have a plan (i.e. this component is being shown on Sign Up)
        const promoText = promoCode.plan === plan.planId ? promoMsg : !currentPlan ? promoFeature : null;
        return (
          <button
            className={classCombine(
              'planSelectButton',
              isCurrent || isUpcoming ? 'isDisabled' : isSelected ? 'isSelected' : null,
              plan.hasPrePayPromotion && !frequencyLabel && 'hasPrePayPromotion'
            )}
            key={plan.planId}
            onClick={() => setHighlightedPlan(plan)}
            disabled={upcomingPlan || isCurrent}
            data-testid="planSelectButton"
          >
            {(isCurrent || isUpcoming) && (
              <div className="planTopLabelContainer">
                <div className="planTopLabel">{isCurrent ? 'Current Plan' : 'Upcoming Plan'}</div>
              </div>
            )}
            <div className="planTopRow">
              <div className="planTopLeft">
                <img
                  className="planSelectRadio"
                  alt={isSelected ? 'Selected' : 'Not Selected'}
                  src={`/account/static/images/${isSelected ? '' : 'un'}selected-radio${
                    isCurrent || isUpcoming ? '-grey' : upcomingPlan ? '-disabled' : ''
                  }.svg`}
                  width="24"
                  height="24"
                />
                <div className={classCombine('planName', promotionPlanNameMobile && 'nameHiddenMobile')}>
                  {HTMLParser(promotionPlanName || HTMLParser(getSimplifiedPlanDisplayName(plan)))}
                </div>
                {promotionPlanNameMobile && (
                  <div className="planName nameOnlyMobile">{HTMLParser(promotionPlanNameMobile)}</div>
                )}
              </div>
              <div className="planTopRight">
                <div className="planPrice">{price || getPlanDisplayPrice(plan.planPrice)}</div>
                <div className="planFreqLabel">{HTMLParser(freqLabel)}</div>
              </div>
            </div>
            {promoText && (
              <div className={classCombine('planFeature', 'firstPlanFeature')}>
                <img
                  className="featureImage"
                  width="18"
                  height="18"
                  alt="Check Mark"
                  src={`/account/static/images/${isCurrent || isUpcoming ? 'grey' : 'red'}-check.svg`}
                />
                <div className="featureText">{HTMLParser(promoText)}</div>
              </div>
            )}
            {mainFeature && (
              <div className={classCombine('planFeature', !promoText && 'firstPlanFeature')}>
                <img
                  className="featureImage"
                  width="18"
                  height="18"
                  alt="Check Mark"
                  src={`/account/static/images/${isCurrent || isUpcoming ? 'grey' : 'red'}-check.svg`}
                />
                <div className="featureText">{HTMLParser(mainFeature)}</div>
              </div>
            )}
          </button>
        );
      })}
      <SubmitButton
        modernLayout
        className="planSelectionSubmitButton"
        text={submitButtonText}
        onClick={() => (selectPlan ? selectPlan(highlightedPlan) : dispatchSelectPlan(highlightedPlan))}
        isDisabled={disableSubmitBtn}
      />
    </div>
  );
};

export const PlanSelectionModal = ({
  show,
  onClose,
  plans,
  selectedPlan,
  selectPlan,
  currentPlan = null,
  upcomingPlan = null,
  validityTill = null,
}) => {
  return (
    <Modal modernLayout isShowing={show} onClose={onClose}>
      <div>
        <div className="planModalTitle">Change Plan</div>
        {
          <PlanSelection
            plans={plans}
            currentPlan={currentPlan}
            upcomingPlan={upcomingPlan}
            selectedPlan={selectedPlan}
            selectPlan={selectPlan}
            validityTill={validityTill}
          />
        }
      </div>
    </Modal>
  );
};
